import {
  startOfWeek,
  startOfMonth,
  startOfYear,
  endOfDay,
  format,
  parseISO,
  isValid,
} from "date-fns";

export const DATE_RANGE_PRESETS = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "week", label: "This week" },
  { id: "month", label: "This month" },
  { id: "year", label: "This year" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom" },
];

const MS_DAY = 86400000;

/** @typedef {{ preset: string, customStart?: string, customEnd?: string }} DateRangeState */

/**
 * Resolve a date-range selection to inclusive millisecond bounds.
 * @param {DateRangeState} range
 * @param {number} [nowMs]
 * @returns {{ startMs: number, endMs: number, label: string, isAllTime: boolean }}
 */
export function resolveDateRange(range, nowMs = Date.now()) {
  const preset = range?.preset || "all";
  const now = new Date(nowMs);
  const endMs = endOfDay(now).getTime();

  if (preset === "all") {
    return { startMs: 0, endMs: Number.MAX_SAFE_INTEGER, label: "All time", isAllTime: true };
  }

  if (preset === "7d") {
    return {
      startMs: nowMs - 7 * MS_DAY,
      endMs,
      label: "Last 7 days",
      isAllTime: false,
    };
  }

  if (preset === "30d") {
    return {
      startMs: nowMs - 30 * MS_DAY,
      endMs,
      label: "Last 30 days",
      isAllTime: false,
    };
  }

  if (preset === "week") {
    return {
      startMs: startOfWeek(now, { weekStartsOn: 1 }).getTime(),
      endMs,
      label: "This week",
      isAllTime: false,
    };
  }

  if (preset === "month") {
    return {
      startMs: startOfMonth(now).getTime(),
      endMs,
      label: "This month",
      isAllTime: false,
    };
  }

  if (preset === "year") {
    return {
      startMs: startOfYear(now).getTime(),
      endMs,
      label: "This year",
      isAllTime: false,
    };
  }

  if (preset === "custom") {
    const start = parseLocalDate(range?.customStart);
    const end = parseLocalDate(range?.customEnd);
    if (start && end && start.getTime() <= end.getTime()) {
      const startMs = start.getTime();
      const endMs = endOfDay(end).getTime();
      const sameYear = start.getFullYear() === end.getFullYear();
      const fmt = sameYear ? "MMM d" : "MMM d, yyyy";
      const label = `${format(start, fmt)} – ${format(end, "MMM d, yyyy")}`;
      return { startMs, endMs, label, isAllTime: false };
    }
    return { startMs: 0, endMs: 0, label: "Custom range", isAllTime: false, invalid: true };
  }

  return { startMs: 0, endMs: Number.MAX_SAFE_INTEGER, label: "All time", isAllTime: true };
}

function parseLocalDate(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const d = parseISO(ymd);
  return isValid(d) ? d : null;
}

/** Filter items by a timestamp accessor and resolved range. */
export function filterByTimestamp(items, getTimestampMs, range) {
  const resolved = resolveDateRange(range);
  if (resolved.isAllTime) return items;
  if (resolved.invalid) return [];
  return items.filter((item) => {
    const t = getTimestampMs?.(item);
    if (!t || t <= 0) return false;
    return t >= resolved.startMs && t <= resolved.endMs;
  });
}
